import type { BigNumber, BigNumberish } from 'ethers';
import { constants } from 'ethers';
import { useContext, useState } from 'react';

import { RequestingSignatureModal } from '../components/common/modal/movie-voting/RequestingSignatureModal.component';
import { ModalStateContext } from '../contexts/app-state-context/modal-state.context';
import { WEB3_CONFIG } from '../lib';
import type { MovieVoting } from '../lib/contracts';
import { MovieVotingFactory } from '../lib/contracts';
import {
  ethToWei,
  MOVIE_VOTING_ADDRESS,
  weiToEth,
} from '../lib/smart-contract-utils';
import { useContract } from './contract.hooks';
import { useStarsContract } from './stars-contract.hooks';

export enum VotingRoundState {
  Active = 0,
  Paused = 1,
  Canceled = 2,
  Executed = 3,
}
export interface MovieRound {
  roundId: string;
  movieIds: string[];
  votes: string[];
  startBlock: string;
  endBlock: string;
  starsRewards: string;
  votingState: VotingRoundState;
}

export const emptyMovieRound: MovieRound = {
  roundId: '',
  movieIds: [],
  votes: [],
  startBlock: '',
  endBlock: '',
  starsRewards: '',
  votingState: VotingRoundState.Canceled,
};

export const useMovieVotingContract = (
  props = {
    overrideAddress: '',
    overrideProvider: '',
    hardOverride: false,
  },
) => {
  const starsContract = useStarsContract();
  const { setModal } = useContext(ModalStateContext);
  const [contractCurrentRoundIndex, setContractCurrentRoundIndex] = useState(0);

  const { contract, ...walletDetails } = useContract<MovieVoting>(
    MovieVotingFactory,
    MOVIE_VOTING_ADDRESS,
    props.overrideAddress,
    props.overrideProvider,
    props.hardOverride,
  );

  const getTotalVotingRounds = async () => {
    if (!contract) {
      return '0';
    }

    try {
      const totalVotingRounds = await contract.totalVotingRounds();

      return totalVotingRounds.toString();
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: {
          file: 'movie-voting.hooks.ts',
          function: 'getTotalVotingRounds',
        },
      });

      return '0';
    }
  };

  const removeVotes = async (
    votingRoundId: string,
    movieId: string,
    starsAmt: string,
    mogulSmartWalletAddress?: string,
  ) => {
    // mogul smart wallet should be some sort of context
    if (!contract) {
      return false;
    }
    try {
      const starsAmtMantissa = ethToWei(starsAmt);
      const trx = await contract.removeVoteForMovie(
        votingRoundId,
        movieId,
        starsAmtMantissa,
        Boolean(mogulSmartWalletAddress),
        mogulSmartWalletAddress ?? walletDetails.address ?? '',
      );
      await trx.wait();
      return true;
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: {
          file: 'movie-voting.hooks.ts',
          function: 'removeVotes',
        },
      });
      return false;
    }
  };

  const getUserStarsForMovie = async (
    votingRoundId: string,
    movieId: string,
  ) => {
    if (!contract) {
      return '0';
    }

    try {
      const movieStarsEntered = await contract.getUserMovieTotalStarsEntered(
        walletDetails.address ?? '',
        movieId,
        votingRoundId,
      );

      return weiToEth(movieStarsEntered);
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: {
          file: 'movie-voting.hooks.ts',
          function: 'getUserStarsForMovie',
        },
      });

      return '0';
    }
  };

  const getVotingRoundInfo = async (votingRoundId: BigNumberish) => {
    if (!contract) {
      return emptyMovieRound;
    }

    try {
      const votingRound = await contract.getVotingRound(votingRoundId);
      const votes: string[] = [];
      const movieIds = votingRound[0].reduce<string[]>((acc, movieId, i) => {
        if (movieId.toString() !== '0') {
          acc.push(movieId.toString());
          votes.push(votingRound[1][i].toString());
        }

        return acc;
      }, []);
      const votingObject = {
        roundId: (votingRoundId as BigNumber).toString(),
        movieIds,
        votes,
        startBlock: votingRound[2].toString(),
        endBlock: votingRound[3].toString(),
        starsRewards: weiToEth(votingRound[4]),
        votingState: votingRound[votingRound.length - 1] as VotingRoundState,
      };

      return votingObject;
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: { file: 'movie-voting.hooks.ts', function: 'getVotingRoundInfo' },
      });

      return emptyMovieRound;
    }
  };

  const getAllVotingRoundsInfo = async (): Promise<{
    allRounds: MovieRound[];
    totalRounds: string;
  }> => {
    const totalRounds = await getTotalVotingRounds();

    if (!totalRounds) {
      return { allRounds: [emptyMovieRound], totalRounds: '0' };
    }

    const allRounds = [];

    for (let i = 0; i < parseInt(totalRounds, 10); i++) {
      // handle error here?
      const roundInfo = await getVotingRoundInfo(i);
      allRounds.push(roundInfo);
    }

    allRounds.sort((a: MovieRound, b: MovieRound) =>
      Math.sign(parseInt(b.endBlock, 10) - parseInt(a.endBlock, 10)),
    );

    return { allRounds, totalRounds };
  };

  const didUserClaimRewardsForRound = async (votingRoundId: string) => {
    if (!contract) {
      return false;
    }

    try {
      const checkDidUserClaimRewards = await contract.didUserClaimRewards(
        walletDetails.address ?? '',
        votingRoundId,
      );

      return checkDidUserClaimRewards;
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: { file: 'movie-voting.hooks.ts', function: 'removeVotes' },
      });

      return false;
    }
  };

  const vote = async (
    votingRoundId: string,
    movieId: string,
    starsAmt: string,
    mogulSmartWalletAddress?: string,
  ): Promise<boolean> => {
    // mogul smart wallet should be some sort of context
    if (!contract) {
      return false;
    }
    try {
      const starsAmtMantissa = ethToWei(starsAmt);
      const trx = await contract.voteForMovie(
        votingRoundId,
        movieId,
        starsAmtMantissa,
        Boolean(mogulSmartWalletAddress),
        mogulSmartWalletAddress ?? walletDetails.address ?? '',
      );

      await trx.wait();

      return true;
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: { file: 'movie-voting.hooks.ts', function: 'vote' },
      });

      return false;
    }
  };

  const calculateStarsRewards = async (roundId: BigNumberish) => {
    if (!contract) {
      return '0';
    }

    try {
      const reward = await contract.calculateStarsRewards(
        walletDetails.address ?? '',
        roundId,
      );

      return weiToEth(reward);
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: { file: 'movie-voting.hooks.ts', function: 'removeVotes' },
      });

      return '0';
    }
  };

  const claimRewardsForRound = async (
    votingRoundId: BigNumberish,
    mogulSmartWalletAddress?: string,
  ) => {
    if (!contract) {
      return false;
    }

    try {
      const trx = await contract.withdrawAndClaimStarsRewards(
        votingRoundId,
        Boolean(mogulSmartWalletAddress),
        mogulSmartWalletAddress ?? walletDetails.address ?? '',
      );

      await trx.wait();

      return true;
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: { file: 'movie-voting.hooks.ts', function: 'removeVotes' },
      });

      return false;
    }
  };

  const claimAllRewards = async (mogulSmartWalletAddress?: string) => {
    if (!contract) {
      return false;
    }

    try {
      for (let i = 0; i < contractCurrentRoundIndex; i++) {
        await claimRewardsForRound(i, mogulSmartWalletAddress);
      }

      return true;
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: { file: 'movie-voting.hooks.ts', function: 'removeVotes' },
      });

      return false;
    }
  };

  const getCurrentBlock = async () => {
    if (!contract) {
      return { timestamp: 0, number: 0 };
    }

    const currentBlock = await contract.provider.getBlockNumber();
    const { number, timestamp } = await contract.provider.getBlock(
      currentBlock,
    );

    return { timestamp, number };
  };

  const getBlockTimestamp = async (blockNumber: number) => {
    if (!contract) {
      return 0;
    }

    const block = await contract.provider.getBlock(blockNumber);

    return block ? block.timestamp : 0;
  };

  const getMovieVotingRounds = async () => {
    const { allRounds, totalRounds } = await getAllVotingRoundsInfo();
    const currentBlock = await getCurrentBlock();

    const pastRounds = [];
    const futureRounds = [];
    let currentRound;
    let startBlock;

    const currentRoundIndex = 0;

    for (const round of allRounds) {
      startBlock = parseInt(round.startBlock, 10);

      if (round.votingState === VotingRoundState.Canceled) {
        pastRounds.push(round);
      } else if (startBlock > currentBlock.number) {
        futureRounds.push(round);
      } else if (startBlock <= currentBlock.number && !currentRound) {
        currentRound = round;
      } else {
        pastRounds.push(round);
      }
    }

    if (
      (!currentRound ||
        parseInt(currentRound.endBlock, 10) <= currentBlock.number) &&
      futureRounds.length
    ) {
      const upcomingRound = futureRounds.pop();

      if (currentRound) {
        pastRounds.splice(0, 0, currentRound);
      }

      currentRound = upcomingRound;
    }

    setContractCurrentRoundIndex(currentRoundIndex);

    return { currentRound, futureRounds, pastRounds, totalRounds };
  };

  return {
    getCurrentBlock,
    getBlockTimestamp,
    address: contract?.address ?? '',
    getMovieVotingRounds,
    walletInfo: walletDetails,
    initialized: contract !== undefined,
    approveStarsContract: async () => {
      await starsContract.approveStakingContract(
        WEB3_CONFIG.contracts[walletDetails.chain.key].movieVoting,
        constants.MaxUint256,
      );

      setModal(RequestingSignatureModal(), true);
    },
    getAllVotingRoundsInfo,
    getTotalVotingRounds,
    getVotingRoundInfo,
    getUserStarsForMovie,
    didUserClaimRewardsForRound,
    vote,
    removeVotes,
    calculateStarsRewards,
    claimRewardsForRound,
    claimAllRewards,
  };
};
